<script>
    import { onMount } from "svelte";

    import { fly } from "svelte/transition";

    import { allGroups, isMobile, user } from "../../stores";

    import { cacheAllGroups } from "../../Utils/utilCache.svelte";

    import {
        replaceURL,
        showError,
        getText,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
    } from "../../Utils/utilFunctions.svelte";

    import { DirectUserRoles } from "../../Utils/utilEnums.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        PageLoading,
        SvelteTable,
        MultiSearchSelect,
        ResetFilterLink,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
    } from "../../Components/Common/index.svelte";

    import {
        editIcon,
        questionnairePropertyMap,
    } from "../../Utils/utilConstants.svelte";

    import CustomiseQuestionnaireModal from "./components/CustomiseQuestionnaireModal.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    export let params = {};

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;

    $: isUserSupport = $user.role == DirectUserRoles.Support;

    $: isUserManager = $user.role == DirectUserRoles.Manager;

    $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

    $: isUserEnableSearchGroup = isUserAdmin || isUserSupport;

    $: isUserEnableSearchDoctor = isUserAdmin || isUserSupport || isUserManager;

    let filters = {},
        showFilters = false;

    let searchGroupsArr = [],
        groupOptions = [];

    let selectedDoctors = [];

    let isGettingPreOpQuestionnaires = false,
        returnedPreOpQuestionnaires = [];

    let isCustomiseQuestionnaireModalOpen = false,
        customiseQuestionnaireType = "",
        selectedQuestionnaire = {},
        settingObj = {};

    let page = 0,
        pageSize = 25,
        count = 0;

    $: columns = [
        {
            key: "name",
            title: "Name",
            headerClass: `text-left px-3 cursor-default rounded-tl-xl ${isUserAdmin || isUserSupport ? "w-5/12" : isUserManager ? "w-2/3" : "w-11/12"}`,
            class: "text-left flex-1 px-3 cursor-default",
            renderValue: (v) => _getDisplayName(v),
            csvContent: (v) => _getDisplayName(v, true),
        },
        {
            key: "groupName",
            title: "Group Name",
            headerClass: "text-left px-3 w-1/4 cursor-default",
            class: "text-left px-3 cursor-default",
            isHidden: !(isUserAdmin || isUserSupport),
            renderValue: (v) => getText(v.groupName),
        },
        {
            key: "accountName",
            title: "Account Name",
            headerClass: "text-left px-3 w-1/4 cursor-default",
            class: "text-left px-3 cursor-default",
            isHidden: !(isUserAdmin || isUserSupport || isUserManager),
            renderValue: (v) => getText(v.accountName),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left px-3 w-[2%] cursor-default rounded-tr-xl",
            class: "text-left px-3 cursor-default",
            renderValue: (v) => _renderActions(v.settings),
            csvExportHidden: true,
        },
    ];

    const searchFilters = [
        {
            key: "preDefineDoctor",
            name: "Doctors",
            filterKey: "filter[doctorId]",
            separateHandle: true,
        },
        {
            key: "doctor",
            name: "Doctors",
            filterKey: "filter[doctorId]",
            separateHandle: true,
        },
        {
            key: "group",
            name: "Groups",
            filterKey: "filter[groupId]",
            separateHandle: true,
        },
    ];

    onMount(async () => {
        if (params.query) updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "filter[doctorId]");

        await loadPreOpQuestionnaires(
            page * pageSize,
            pageSize,
            _getSearchParams(),
        );

        if (isUserEnableSearchGroup) {
            await cacheAllGroups();

            groupOptions = $allGroups.map(({ id, name }) => ({
                id,
                type: name,
            }));
        }
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                searchFilters.forEach((sf) => {
                    if (arr[i].includes(sf.filterKey)) {
                        filters[sf.key] = arr[i].split("=")[1];
                        if (
                            arr[i].includes("groupId") &&
                            isUserEnableSearchGroup
                        ) {
                            searchGroupsArr = filters[sf.key]
                                .split(",")
                                .map((id) => Number(id));
                            filters[sf.filterKey] = searchGroupsArr.join(",");
                        }

                        if (
                            arr[i].includes("doctorId") &&
                            isUserEnableSearchDoctor
                        ) {
                            if (isNaN(arr[i].split("=")[1])) {
                                filters[sf.filterKey] = arr[i].split("=")[1];
                                selectedDoctors = filters[sf.filterKey]
                                    .split(",")
                                    .map((id) => Number(id));
                            } else {
                                filters[sf.filterKey] = Number(
                                    arr[i].split("=")[1],
                                );
                                selectedDoctors = [
                                    Number(filters[sf.filterKey]),
                                ];
                            }
                        }
                    }
                });
            }

            updateURL();
        }
    };

    const loadPreOpQuestionnaires = async (start, limit, params) => {
        isGettingPreOpQuestionnaires = true;

        await apiMethods.getMethod
            .getPreOpQuestionnaires(start, limit, params)
            .then((res) => _handlePreOpQuestionnaires(res))
            .catch((err) => showError(err));

        isGettingPreOpQuestionnaires = false;
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const handleFilterSelect = () => {
        if (searchGroupsArr.length && isUserEnableSearchGroup)
            filters["filter[groupId]"] = searchGroupsArr.join(",");
        else delete filters["filter[groupId]"];

        if (selectedDoctors.length && isUserEnableSearchDoctor)
            filters["filter[doctorId]"] = selectedDoctors.join(",");
        else delete filters["filter[doctorId]"];

        page = 0;
        updateURL();
        loadPreOpQuestionnaires(0, pageSize, _getSearchParams());
    };

    const updateURL = () =>
        replaceURL(
            `/PreOpQuestionnaires/page=${page}&size=${pageSize}${_getParamsString()}`,
        );

    const toggleFilters = () => (showFilters = !showFilters);

    const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadPreOpQuestionnaires(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadPreOpQuestionnaires(0, newPageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (className.includes("urlLink")) window.open(rowData.url, "_blank");
        else if (className.includes("editQuestionnaireIcon"))
            _openCustomiseQuestionnaireModal(rowData, "edit");
        else if (className.includes("customiseForGroupIcon"))
            _openCustomiseQuestionnaireModal(rowData, "group");
        else if (className.includes("customiseForDoctorIcon"))
            _openCustomiseQuestionnaireModal(rowData, "doctor");
    };

    const handleUpdateQuestionnaire = async (result, type) => {
        selectedQuestionnaire = {
            ...selectedQuestionnaire,
            ...result,
        };

        if (type == "edit") await updateQuestionnaire(selectedQuestionnaire);
        else await createCustomiseQuestionnaire(selectedQuestionnaire);
    };

    const updateQuestionnaire = async (result) => {
        await apiMethods.putMethod
            .updatePreOpQuestionnaire(result)
            .then((res) => {
                if (!res || !res.id || res.error)
                    showError(
                        res.error ??
                            "Failed to update questionnaire, please refresh the page and try again and contact support if the issue persists.",
                    );
                else
                    loadPreOpQuestionnaires(
                        page * pageSize,
                        pageSize,
                        _getSearchParams(),
                    );

                _closeCustomiseQuestionnaireModal();
            })
            .catch((err) => showError(err));
    };

    const createCustomiseQuestionnaire = async (result) => {
        await apiMethods.postMethod
            .createPreOpQuestionnaire(result)
            .then((res) => {
                if (!res || !res.id || res.error)
                    showError(
                        res.error ??
                            "Failed to customise selected questionnaire, please refresh the page and try again and contact support if the issue persists.",
                    );
                else
                    loadPreOpQuestionnaires(
                        page * pageSize,
                        pageSize,
                        _getSearchParams(),
                    );

                _closeCustomiseQuestionnaireModal();
            })
            .catch((err) => showError(err));
    };

    // HELPER FUNCTIONS
    const _handlePreOpQuestionnaires = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load PreOp Questionnaires, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            returnedPreOpQuestionnaires = [...res.results];
            count = res.total;
        }
    };

    const _getDisplayName = (data, isCsvContent = false) => {
        const { name, accountName, groupName } = data;

        if (isUserDoctor)
            return isCsvContent
                ? `${name}`
                : `<p class="urlLink cursor-pointer hover:underline hover:font-bold">${name}</p>`;

        if (accountName)
            return isCsvContent
                ? `${name} (Doctor: ${accountName})`
                : `<p class="urlLink cursor-pointer hover:underline hover:font-bold">${name} (Doctor: ${accountName})</p>`;

        if (groupName)
            return isCsvContent
                ? `${name} (Group: ${groupName})`
                : `<p class="urlLink cursor-pointer hover:underline hover:font-bold">${name} (Group: ${groupName})</p>`;

        return isCsvContent
            ? `${name}`
            : `<p class="urlLink cursor-pointer hover:underline hover:font-bold">${name}</p>`;
    };

    const _renderActions = (settings) => {
        const questionnaireCustomised = settings != "";

        if (questionnaireCustomised)
            return `
                <div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="editQuestionnaireIcon tooltip tooltip-secondary tooltip-left cursor-pointer flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit">
                        <i class="editQuestionnaireIcon cursor-pointer ${editIcon}"></i>
                    </div>
                </div>
            `;

        const customiseForGroupIcon = `<div class="customiseForGroupIcon tooltip tooltip-secondary tooltip-left cursor-pointer flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Customise ${isUserManager ? "" : "for Group"}">
                            <i class="customiseForGroupIcon cursor-pointer fa-solid fa-users-viewfinder fa-sm"></i>
                        </div>`;

        const customiseForDoctorIcon = `<div class="customiseForDoctorIcon tooltip tooltip-secondary tooltip-left cursor-pointer flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Customise ${isUserDoctor ? "" : "for Doctor"}">
                            <i class="customiseForDoctorIcon cursor-pointer fa fa-user-doctor"></i>
                        </div>`;

        return `
            <div class="flex flex-1 items-center justify-start gap-x-2">
                ${!isUserDoctor ? `${customiseForGroupIcon}` : ""}

                ${customiseForDoctorIcon}
            </div>
        `;
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters["filter[groupId]"] && isUserEnableSearchGroup)
            params["filter[groupId]"] = filters["filter[groupId]"];

        if (filters["filter[doctorId]"] && isUserEnableSearchDoctor)
            params["filter[doctorId]"] = filters["filter[doctorId]"];

        return params;
    };

    const _getParamsString = () => {
        let str = "";

        const params = _getSearchParams();

        if (params) {
            searchFilters.forEach((sf) => {
                if (params[sf.filterKey] || params[sf.filterKey] === 0)
                    str += `&${sf.filterKey}=` + params[sf.filterKey];
            });
        }

        return encodeSearchParams(str);
    };

    const _openCustomiseQuestionnaireModal = (data, type = "") => {
        const { name, groupId, accountId, settings, id, url } = data;

        selectedQuestionnaire = { ...data };

        if (!settings) settingObj = _initQuestionnaireSettings(data);
        else {
            const disableSettings = url
                .split("?")[1]
                ?.split("&")
                .map((s) => s.replace("=0", ""));

            settingObj = {
                name,
                groupId,
                accountId,
                id,
                url,
            };

            if (settings)
                settingObj = {
                    ...settingObj,
                    ...JSON.parse(settings),
                };
            else
                for (const key of Object.keys(questionnairePropertyMap)) {
                    settingObj[key] = disableSettings?.length
                        ? disableSettings.includes(key)
                            ? 0
                            : 1
                        : 1;
                }
        }

        if (Object.prototype.toString.call(settingObj) === "[object Object]") {
            isCustomiseQuestionnaireModalOpen = true;
            customiseQuestionnaireType = type;
        } else _closeCustomiseQuestionnaireModal();
    };

    const _closeCustomiseQuestionnaireModal = () => {
        isCustomiseQuestionnaireModalOpen = false;
        customiseQuestionnaireType = "";
        selectedQuestionnaire = {};
        settingObj = {};
    };

    const _initQuestionnaireSettings = ({ name, groupId, accountId, url }) => {
        const res = { name, groupId, accountId, url };

        Object.keys(questionnairePropertyMap).forEach((key) => (res[key] = 1));

        return res;
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-28 flex flex-row gap-x-3"
            on:click={toggleFilters}
        >
            Filters
            <i
                style="font-size:20px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>
    {/if}
    {#if showFilters || !$isMobile}
        <div
            class="flex flex-0 {$isMobile
                ? 'flex-col gap-y-4  items-start'
                : 'justify-between items-end'}"
            in:filterTransition={{ duration: 200, y: -100 }}
        >
            <div class="flex gap-4 flex-wrap items-end">
                {#each searchFilters as searchFilter}
                    {#if searchFilter.key == "preDefineDoctor"}
                        {#if isUserEnableSearchDoctor}
                            <DefaultDoctorFilter
                                bind:filter={filters}
                                filterPropertyName={"filter[doctorId]"}
                                onChangeFunc={() => {
                                    selectedDoctors =
                                        filters["filter[doctorId]"].split(",");
                                    handleFilterSelect();
                                }}
                            />
                        {/if}
                    {:else if searchFilter.key == "doctor"}
                        {#if isUserEnableSearchDoctor}
                            <DoctorMultiSearchSelect
                                bind:searchResult={selectedDoctors}
                                onChangeFunc={handleSelectDoctor}
                                inputClass="w-32"
                                zIndex="3"
                            />
                        {/if}
                    {:else if searchFilter.key == "group"}
                        {#if isUserEnableSearchGroup}
                            <MultiSearchSelect
                                title={searchFilter.name}
                                bind:searchResult={searchGroupsArr}
                                optionsArr={groupOptions}
                                inputID="emailTemplateGroupSearchInput"
                                onChangeFunc={handleFilterSelect}
                                inputClass="w-32"
                            />
                        {/if}
                    {/if}
                {/each}

                <ResetFilterLink
                    bind:filter={filters}
                    onResetFilter={() => {
                        searchGroupsArr = [];
                        selectedDoctors = [];
                        handleFilterSelect();
                        clearFilterDoctor();
                    }}
                />
            </div>
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingPreOpQuestionnaires}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={returnedPreOpQuestionnaires}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnedPreOpQuestionnaires}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns: columns.filter((col) => !col.isHidden),
            data: returnedPreOpQuestionnaires,
            fileName: "Pre-op Questionnaires",
            allDataApi: async () =>
                await apiMethods.getMethod.getPreOpQuestionnaires(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

<CustomiseQuestionnaireModal
    isModalOpen={isCustomiseQuestionnaireModalOpen}
    type={customiseQuestionnaireType}
    {settingObj}
    closeModal={_closeCustomiseQuestionnaireModal}
    confirmAction={handleUpdateQuestionnaire}
/>
